import { useCallback, useEffect, useMemo } from 'react';
import { AreaSeries as VisxAreaSeries, AreaStack } from '@visx/xychart';

import { DIM_OPACITY_FACTOR } from '../../constants';
import type { AreaStackSeriesProps } from '../../types/AreaStackSeriesProps';
import type { ChartDataPoint, ChartDataPointValue } from '../../types/ChartDataPoint';
import { useChartContext } from '../ChartContext';

import { StackLineMarkers } from './StackLineMarkers';

/**
 * AreaStackSeries component for composable LineChart.
 *
 * Renders multiple series as stacked areas using visx's AreaStack with
 * AreaSeries children. Registers each series with the tooltip system for hover display.
 *
 * @example
 * ```tsx
 * <LineChart data={data}>
 *   <AreaStackSeries
 *     series={[
 *       { seriesKey: 'cpu', name: 'CPU', dataKey: 'cpu', color: '#4d77ff' },
 *       { seriesKey: 'memory', name: 'Memory', dataKey: 'memory', color: '#00c853' },
 *     ]}
 *     fillOpacity={0.1}
 *     strokeWidth={2}
 *   />
 *   <Tooltip enabled />
 * </LineChart>
 * ```
 */
export function AreaStackSeries({
  series,
  fillOpacity = 0.1,
  strokeWidth = 2,
  nullHandling = 'Zero',
  showStackLines = true,
}: AreaStackSeriesProps) {
  const {
    visibleData,
    xDataKey,
    xScaleType,
    colorScale,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
    chartClipPathId,
  } = useChartContext();

  // Register each series with tooltip system (respecting showInTooltip)
  useEffect(() => {
    for (const config of series) {
      if (config.showInTooltip === false) {
        continue;
      }
      const color = config.color ?? colorScale(config.seriesKey);
      registerSeriesDisplayMetadata(config.seriesKey, {
        name: config.name,
        color,
        dataKey: config.dataKey,
        tooltipRowIcon: config.tooltipRowIcon,
      });
    }
    return () => {
      for (const config of series) {
        if (config.showInTooltip === false) {
          continue;
        }
        unregisterSeriesDisplayMetadata(config.seriesKey);
      }
    };
  }, [series, colorScale, registerSeriesDisplayMetadata, unregisterSeriesDisplayMetadata]);

  // Build x accessor
  const getXValue = useCallback(
    (d: ChartDataPoint | undefined): ChartDataPointValue => {
      if (!d) {
        return null;
      }
      return d[xDataKey] ?? null;
    },
    [xDataKey],
  );

  const xAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      const value = getXValue(d);
      if (value === null || value === undefined) {
        return xScaleType === 'band' ? '' : 0;
      }
      return xScaleType === 'band' ? String(value) : value;
    },
    [getXValue, xScaleType],
  );

  // Prepare data for stacking.
  // When nullHandling is 'Zero', replace null/undefined/non-finite values with 0 so every
  // data point participates in the stack. For 'Hidden' and 'Connected', preserve null values
  // so the tooltip can distinguish missing data from real zeros.
  const stackedData = useMemo(() => {
    if (nullHandling === 'Zero') {
      const keys = series.map((s) => s.dataKey);
      return visibleData.map((datum) => {
        const result: ChartDataPoint = { ...datum };
        for (const key of keys) {
          const value = datum[key];
          if (value === null || value === undefined || (typeof value === 'number' && !Number.isFinite(value))) {
            result[key] = 0;
          }
        }
        return result;
      });
    }
    // 'Hidden' / 'Connected': pass data through without rewriting nulls
    return visibleData;
  }, [visibleData, series, nullHandling]);

  // Build y accessor keyed by dataKey.
  // The accessor must always return a number for d3's stack layout.
  const defaultYAccessor = useCallback((_d: ChartDataPoint | undefined) => 0, []);

  const yAccessorByKey = useMemo(() => {
    const map = new Map<string, (d: ChartDataPoint | undefined) => number>();
    for (const config of series) {
      const dataKey = config.dataKey;
      map.set(dataKey, (d: ChartDataPoint | undefined) => {
        if (!d) {
          return 0;
        }
        const value = d[dataKey];
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
      });
    }
    return map;
  }, [series]);

  if (series.length === 0) {
    return null;
  }

  return (
    <>
      <AreaStack>
        {series.map((config) => {
          const color = config.color ?? colorScale(config.seriesKey);
          const yAccessor = yAccessorByKey.get(config.dataKey) ?? defaultYAccessor;
          const isDimmed = config.dimmed ?? false;
          return (
            <VisxAreaSeries
              key={config.seriesKey}
              dataKey={config.dataKey}
              data={stackedData}
              xAccessor={xAccessor}
              yAccessor={yAccessor}
              fill={color}
              fillOpacity={isDimmed ? fillOpacity * DIM_OPACITY_FACTOR : fillOpacity}
              renderLine
              lineProps={{
                stroke: color,
                strokeWidth,
                strokeOpacity: isDimmed ? DIM_OPACITY_FACTOR : 1,
                strokeLinejoin: 'round',
                strokeLinecap: 'round',
              }}
              clipPath={`url(#${chartClipPathId})`}
            />
          );
        })}
      </AreaStack>
      {showStackLines && (
        <StackLineMarkers
          series={series}
          data={stackedData}
          xDataKey={xDataKey}
          xScaleType={xScaleType}
          colorScale={colorScale}
        />
      )}
    </>
  );
}

AreaStackSeries.displayName = 'AreaStackSeries';
